// src/pages/AdminDevelopmentFormPage.tsx
import React, { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { FiArrowLeft, FiSave } from 'react-icons/fi';
import { useTranslation } from 'react-i18next';
import styles from './AdminDevelopmentFormPage.module.css';
import {
  AdminDevelopmentsService,
  CreateDevelopmentDto,
  DevelopmentStatus,
  ProjectState,
  AdminDevelopmentImage
} from '../services/adminDevelopments.service';
import { useAuth } from '../context/AuthContext';
import { useNotification } from '../context/NotificationContext';
import { DevelopmentBlocksService } from '../services/developmentBlocks.service';
import { DevelopmentDocumentsService, DevelopmentDocument } from '../services/developmentDocuments.service';
import { DevelopmentUploadsService } from '../services/developmentUploads.service';
import { DevelopmentImagesService } from '../services/developmentImages.service';
import { useKycDocuments } from '../hooks/useKycDocuments';

type Mode = 'create' | 'edit';

const defaultForm: CreateDevelopmentDto = {
  developer_id: null,
  name: '',
  slug: '',
  summary: '',
  description: '',
  country: 'México',
  state: '',
  city: '',
  address: '',
  status: 'PLANNING',
  project_state: 'DRAFT',
  progress_pct: 0,
  seo_title: '',
  seo_description: '',
  og_image_url: '',
  video_url: '',
  investment_start_at: null,
  investment_end_at: null,
  min_blocks_per_order: null,
  max_blocks_per_user: null,
  funding_goal: null,
  term_months: null,
  sort_order: 0,
  publish_from: null,
  publish_to: null,
  is_active: true
};

const buildSlugFromName = (value: string) =>
  value
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-|-$/g, '');

export default function AdminDevelopmentFormPage({ mode }: { mode: Mode }) {
  const { t } = useTranslation();
  const { user } = useAuth();
  const { addNotification } = useNotification();
  const navigate = useNavigate();
  const params = useParams<{ id: string }>();
  const id = params.id ? Number(params.id) : null;
  const isDeveloper = user?.role === 'developer';
  const needsKycCheck = isDeveloper && mode === 'create';
  const { statuses: kycStatuses, loading: kycDocsLoading, error: kycDocsError } = useKycDocuments({
    enabled: needsKycCheck,
  });

  const [form, setForm] = useState<CreateDevelopmentDto>(defaultForm);
  const [loading, setLoading] = useState(mode === 'edit');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [block, setBlock] = useState({ description: '', unit_price: 0, available_qty: 0 });
  const [documents, setDocuments] = useState<DevelopmentDocument[]>([]);
  const [existingImages, setExistingImages] = useState<AdminDevelopmentImage[]>([]);
  const [coverImageId, setCoverImageId] = useState<number | null>(null);
  const [docTitle, setDocTitle] = useState('');
  const [docUrl, setDocUrl] = useState('');
  const [imageFiles, setImageFiles] = useState<File[]>([]);
  const [docFiles, setDocFiles] = useState<File[]>([]);
  const [imageCoverIndex, setImageCoverIndex] = useState(0);
  const [imagePreviews, setImagePreviews] = useState<Array<{ file: File; url: string }>>([]);
  const [imageMode, setImageMode] = useState<'upload' | 'links'>('upload');
  const [imageLinks, setImageLinks] = useState<string[]>(['', '', '']);

  const title = useMemo(
    () => (mode === 'create' ? t('admin.project_new_title') || 'Nuevo proyecto' : t('admin.project_edit_title') || 'Editar proyecto'),
    [mode, t],
  );
  const backHref = useMemo(() => {
    if (!isDeveloper) return '/admin/proyectos';
    return '/developer/panel';
  }, [isDeveloper]);
  const postSaveRedirect = useMemo(() => {
    if (!isDeveloper) return '/admin/proyectos';
    return '/developer/panel';
  }, [isDeveloper]);

  const statusOptions = useMemo<{ value: DevelopmentStatus; label: string }[]>(() => [
    { value: 'PLANNING', label: t('admin.phase_planning') || 'Planeación' },
    { value: 'PRE_SALE', label: t('admin.phase_pre_sale') || 'Preventa' },
    { value: 'UNDER_CONSTRUCTION', label: t('admin.phase_under_construction') || 'En construcción' },
    { value: 'COMPLETED', label: t('admin.phase_completed') || 'Finalizado' },
    { value: 'CANCELLED', label: t('admin.phase_cancelled') || 'Cancelado' }
  ], [t]);

  const projectStateOptions = useMemo<{ value: ProjectState; label: string }[]>(() => [
    { value: 'DRAFT', label: t('admin.project_state_draft') || 'Borrador' },
    { value: 'PUBLISHED', label: t('admin.project_state_published') || 'Publicado' },
    { value: 'IN_EXECUTION', label: t('admin.project_state_in_execution') || 'En ejecución' },
    { value: 'FINISHED', label: t('admin.project_state_finished') || 'Finalizado' },
    { value: 'CANCELLED', label: t('admin.project_state_cancelled') || 'Cancelado' }
  ], [t]);

  const toDateTimeLocal = (value?: string | null) => {
    if (!value) return '';
    const d = new Date(value);
    if (Number.isNaN(d.getTime())) return '';
    return d.toISOString().slice(0, 16);
  };

  useEffect(() => {
    const next = imageFiles.map((file) => ({ file, url: URL.createObjectURL(file) }));
    setImagePreviews(next);
    return () => {
      next.forEach((p) => URL.revokeObjectURL(p.url));
    };
  }, [imageFiles]);

  useEffect(() => {
    if (mode !== 'edit') return;
    if (!id || !Number.isFinite(id)) {
      setError(t('admin.invalid_id') || 'ID inválido.');
      setLoading(false);
      return;
    }

    let alive = true;
    setLoading(true);
    setError(null);

    const loadDetail = async () => {
      try {
        const res = await AdminDevelopmentsService.getById(id);
        if (!alive) return;
        if (!res || (res as any).ok === false) {
          setError(((res as any)?.message as string) || t('admin.load_error') || 'No se pudo cargar la información.');
          return;
        }

        const d = (res as any).data;
        setForm({
          developer_id: d?.developer_id ?? null,
          name: d?.name ?? '',
          slug: d?.slug ?? '',
          summary: d?.summary ?? '',
          description: d?.description ?? '',
          country: d?.country ?? 'México',
          state: d?.state ?? '',
          city: d?.city ?? '',
          address: d?.address ?? '',
          latitude: d?.latitude ?? null,
          longitude: d?.longitude ?? null,
          status: (d?.status ?? 'PLANNING') as DevelopmentStatus,
          project_state: (d?.project_state ?? 'DRAFT') as ProjectState,
          progress_pct: d?.progress_pct ?? 0,
          seo_title: d?.seo_title ?? '',
          seo_description: d?.seo_description ?? '',
          og_image_url: d?.og_image_url ?? '',
          video_url: d?.video_url ?? '',
          sort_order: d?.sort_order ?? 0,
          risk_level_id: d?.risk_level_id ?? null,
          publish_from: d?.publish_from ?? null,
          publish_to: d?.publish_to ?? null,
          investment_start_at: d?.investment_start_at ?? null,
          investment_end_at: d?.investment_end_at ?? null,
          min_blocks_per_order: d?.min_blocks_per_order ?? null,
          max_blocks_per_user: d?.max_blocks_per_user ?? null,
          funding_goal: d?.funding_goal ?? null,
          term_months: d?.term_months ?? null,
          is_active: d?.is_active ?? true
        });
        const imgs = Array.isArray(d?.images) ? (d.images as AdminDevelopmentImage[]) : [];
        const sortedImgs = [...imgs].sort((a, b) => (a.sort_order ?? 0) - (b.sort_order ?? 0));
        setExistingImages(sortedImgs);
        const cover = sortedImgs.find(i => i.is_cover);
        setCoverImageId(cover?.id ?? null);

        try {
          const blocksRes = await DevelopmentBlocksService.list(id as number);
          const list = (blocksRes as any)?.data;
          if (Array.isArray(list) && list.length > 0) {
            const first = list[0];
            setBlock({
              description: first.description || '',
              unit_price: Number(first.unit_price || 0),
              available_qty: Number(first.available_qty || 0)
            });
          }
        } catch {
          // ignore
        }

        try {
          const docsRes = await DevelopmentDocumentsService.list(id as number);
          const list = (docsRes as any)?.data;
          setDocuments(Array.isArray(list) ? list : []);
        } catch {
          setDocuments([]);
        }
      } catch {
        if (!alive) return;
        setError(t('admin.load_error') || 'No se pudo cargar la información.');
      } finally {
        if (!alive) return;
        setLoading(false);
      }
    };

    loadDetail();

    return () => {
      alive = false;
    };
  }, [id, mode, t]);

  const update = <K extends keyof CreateDevelopmentDto>(key: K, value: CreateDevelopmentDto[K]) => {
    setForm(prev => {
      if (key === 'name') {
        const name = String(value || '');
        return { ...prev, name, slug: buildSlugFromName(name) };
      }

      return { ...prev, [key]: value };
    });
  };

  const addDocument = async () => {
    const title = docTitle.trim();
    const url = docUrl.trim();
    if (!title || !url) return;

    if (mode === 'edit' && id) {
      try {
        const res = await DevelopmentDocumentsService.create(id, { title, file_url: url });
        const created = (res as any)?.data;
        if (created) {
          setDocuments(prev => [...prev, created]);
          setDocTitle('');
          setDocUrl('');
        }
      } catch {
        // ignore
      }
      return;
    }

    setDocuments(prev => [
      ...prev,
      {
        id: Date.now(),
        development_id: 0,
        title,
        file_url: url,
        sort_order: 0
      } as DevelopmentDocument
    ]);
    setDocTitle('');
    setDocUrl('');
  };

  const onSelectImages = (files: FileList | null) => {
    if (!files) return;
    const list = Array.from(files);
    const next = [...imageFiles, ...list].slice(0, 3);
    setImageFiles(next);
    if (imageCoverIndex >= next.length) {
      setImageCoverIndex(0);
    }
  };

  const onSelectDocs = (files: FileList | null) => {
    if (!files) return;
    const list = Array.from(files);
    setDocFiles(prev => [...prev, ...list]);
  };

  const moveNewImage = (index: number, direction: -1 | 1) => {
    const next = [...imageFiles];
    const target = index + direction;
    if (target < 0 || target >= next.length) return;
    const [item] = next.splice(index, 1);
    next.splice(target, 0, item);
    setImageFiles(next);
    if (imageCoverIndex === index) setImageCoverIndex(target);
    else if (imageCoverIndex === target) setImageCoverIndex(index);
  };

  const updateImageLink = (index: number, value: string) => {
    const next = [...imageLinks];
    next[index] = value;
    setImageLinks(next);
  };

  const applyExistingOrder = async (next: AdminDevelopmentImage[], coverId?: number | null) => {
    setExistingImages(next);
    setCoverImageId(coverId ?? null);
    if (mode !== 'edit' || !id) return;
    if (next.length === 0) return;
    await DevelopmentImagesService.reorder(id, {
      ids: next.map(i => i.id),
      coverId: coverId ?? undefined
    }).catch(() => null);
  };

  const moveExisting = (index: number, direction: -1 | 1) => {
    const next = [...existingImages];
    const target = index + direction;
    if (target < 0 || target >= next.length) return;
    const [item] = next.splice(index, 1);
    next.splice(target, 0, item);
    applyExistingOrder(next, coverImageId);
  };

  const setExistingCover = (imageId: number) => {
    const next = existingImages.map(img => ({ ...img, is_cover: img.id === imageId }));
    applyExistingOrder(next, imageId);
  };

  const removeExistingImage = async (imageId: number) => {
    if (mode === 'edit' && id) {
      await DevelopmentImagesService.remove(id, imageId).catch(() => null);
    }
    const next = existingImages.filter(i => i.id !== imageId);
    const nextCover = next.find(i => i.is_cover)?.id ?? null;
    applyExistingOrder(next, nextCover);
  };

  const removeDocument = async (doc: DevelopmentDocument) => {
    if (mode === 'edit' && id) {
      try {
        await DevelopmentDocumentsService.remove(id, doc.id);
      } catch {
        // ignore
      }
    }
    setDocuments(prev => prev.filter(d => d.id !== doc.id));
  };

  const onSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSaving(true);
    setError(null);

    try {
      const normalizedName = (form.name || '').trim();
      const normalizedSlug = normalizedName ? buildSlugFromName(normalizedName) : '';

      const resolvedProjectState: ProjectState =
        isDeveloper && mode === 'create'
          ? 'DRAFT'
          : (form.project_state || 'DRAFT');

      const payload: CreateDevelopmentDto = {
        ...form,
        developer_id: mode === 'create' ? (isDeveloper ? user?.id ?? null : null) : form.developer_id,
        project_state: resolvedProjectState,
        name: normalizedName,
        slug: normalizedSlug || undefined,
        summary: typeof form.summary === 'string' ? form.summary.trim() : form.summary,
        description: typeof form.description === 'string' ? form.description.trim() : form.description,
        country: (form.country || '').trim(),
        state: (form.state || '').trim(),
        city: (form.city || '').trim(),
        address: typeof form.address === 'string' ? form.address.trim() : form.address,
        seo_title: typeof form.seo_title === 'string' ? form.seo_title.trim() : form.seo_title,
        seo_description: typeof form.seo_description === 'string' ? form.seo_description.trim() : form.seo_description,
        og_image_url: typeof form.og_image_url === 'string' ? form.og_image_url.trim() : form.og_image_url,
        video_url: typeof form.video_url === 'string' ? form.video_url.trim() : form.video_url,
        investment_start_at: form.investment_start_at || null,
        investment_end_at: form.investment_end_at || null,
        min_blocks_per_order: form.min_blocks_per_order ?? null,
        max_blocks_per_user: form.max_blocks_per_user ?? null,
        funding_goal: form.funding_goal ?? null,
        term_months: form.term_months ?? null
      };

      if (!payload.name || !payload.country || !payload.state || !payload.city) {
        const msg = t('admin.required_fields') || 'Completa los campos obligatorios.';
        setError(msg);
        addNotification(msg, 'error');
        return;
      }

      const res =
        mode === 'create'
          ? await AdminDevelopmentsService.create(payload)
          : await AdminDevelopmentsService.update(id as number, payload);

      if (!res || (res as any).ok === false) {
        const msg = ((res as any)?.message as string) || t('admin.save_error') || 'No se pudo guardar.';
        setError(msg);
        addNotification(msg, 'error');
        return;
      }

      const devId = mode === 'create' ? Number((res as any)?.data?.id ?? (res as any)?.id ?? id) : (id as number);

      if (devId) {
        await DevelopmentBlocksService.upsertDefault(devId, {
          description: block.description || '',
          unit_price: Number(block.unit_price || 0),
          available_qty: Number(block.available_qty || 0)
        }).catch(() => null);

        if (mode === 'create' && documents.length > 0) {
          for (const doc of documents) {
            await DevelopmentDocumentsService.create(devId, {
              title: doc.title,
              file_url: doc.file_url
            }).catch(() => null);
          }
        }

        if (imageMode === 'upload' && imageFiles.length > 0) {
          const createdIds: number[] = [];
          for (const file of imageFiles) {
            const resUpload = await DevelopmentUploadsService.uploadImage(devId, file).catch(() => null);
            const created = (resUpload as any)?.data;
            if (created?.id) createdIds.push(Number(created.id));
          }
          if (createdIds.length > 0) {
            const coverId = createdIds[Math.min(imageCoverIndex, createdIds.length - 1)];
            await DevelopmentImagesService.reorder(devId, { ids: createdIds, coverId }).catch(() => null);
          }
        }

        if (imageMode === 'links') {
          const urls = imageLinks.map(s => s.trim()).filter(Boolean).slice(0, 3);
          if (urls.length > 0) {
            const createdIds: number[] = [];
            for (let i = 0; i < urls.length; i++) {
              const url = urls[i];
              const resCreate = await DevelopmentImagesService.create(devId, {
                image_url: url,
                is_cover: i === 0,
                sort_order: i
              }).catch(() => null);
              const created = (resCreate as any)?.data;
              if (created?.id) createdIds.push(Number(created.id));
            }
            if (createdIds.length > 0) {
              await DevelopmentImagesService.reorder(devId, { ids: createdIds, coverId: createdIds[0] }).catch(() => null);
            }
          }
        }

        if (docFiles.length > 0) {
          for (const file of docFiles) {
            await DevelopmentUploadsService.uploadDocument(devId, file, { title: file.name }).catch(() => null);
          }
        }
      }

      const successMessage =
        isDeveloper && mode === 'create'
          ? 'Proyecto guardado. Ahora sube los documentos requeridos para que un administrador pueda revisarlo y publicarlo.'
          : (t('admin.save_success') || 'Proyecto guardado correctamente.');
      addNotification(successMessage, 'success');
      navigate(isDeveloper && mode === 'create' && devId ? `/developer/proyectos/${devId}/documentos` : postSaveRedirect);
    } catch (err: any) {
      const raw = err?.message || '';
      const isNetwork = /Failed to fetch|NetworkError|ERR_CONNECTION_REFUSED/i.test(raw);
      const msg = isNetwork
        ? (t('common.api_unreachable') || 'No se pudo conectar con el servidor.')
        : (raw || t('admin.save_error') || 'No se pudo guardar.');
      setError(msg);
      addNotification(msg, 'error');
    } finally {
      setSaving(false);
    }
  };

  const renderKycGate = (subtitle: string, message: string) => (
    <div className={styles.container}>
      <header className={styles.header}>
        <div className={styles.headerLeft}>
          <Link to={backHref} className={styles.backLink}>
            <FiArrowLeft /> Volver
          </Link>
          <div className={styles.headerText}>
            <h1 className={styles.title}>{title}</h1>
            <p className={styles.subtitle}>{subtitle}</p>
          </div>
        </div>
        <button type="button" className={styles.primaryButton} onClick={() => navigate('/perfil')}>
          Ir a Perfil
        </button>
      </header>
      <div className={styles.errorBanner}>{message}</div>
    </div>
  );

  if (needsKycCheck) {
    const hasRejectedDocs = kycStatuses.some((doc) => doc.status === 'REJECTED');
    const hasMissingDocs = kycStatuses.length === 0 || kycStatuses.some((doc) => doc.status === 'MISSING');
    const allDocsApproved = kycStatuses.length > 0 && kycStatuses.every((doc) => doc.status === 'APPROVED');

    if (kycDocsLoading) {
      return renderKycGate(
        'Verificando documentos KYC...',
        'Estamos validando el estado de tus documentos. Intenta nuevamente en unos segundos.',
      );
    }

    if (kycDocsError) {
      return renderKycGate(
        'No pudimos verificar tus documentos.',
        kycDocsError || 'No se pudo validar la documentación requerida.',
      );
    }

    if (hasMissingDocs) {
      return renderKycGate(
        'Sube tus documentos KYC para crear un proyecto.',
        'Debes cargar identificación oficial por ambos lados y comprobante de domicilio antes de agregar un nuevo proyecto.',
      );
    }

    if (hasRejectedDocs) {
      return renderKycGate(
        'Actualiza tus documentos KYC.',
        'Uno o más documentos KYC fueron rechazados. Reemplázalos desde tu perfil para poder crear proyectos.',
      );
    }

    if (!allDocsApproved) {
      return renderKycGate(
        'Tus documentos KYC están en revisión.',
        'Ya cargaste tus documentos. Podrás crear un proyecto cuando el administrador apruebe tu verificación KYC.',
      );
    }
  }

  if (loading) {
    return (
      <div className={styles.container} aria-busy="true" aria-live="polite">
        <div className={styles.skeletonHeader} />
        <div className={styles.skeletonCard} />
      </div>
    );
  }

  return (
    <div className={styles.container}>
      <header className={styles.header}>
        <div className={styles.headerLeft}>
          <Link to={backHref} className={styles.backLink}>
            <FiArrowLeft /> {t('common.back') || 'Volver'}
          </Link>
          <div className={styles.headerText}>
            <h1 className={styles.title}>{title}</h1>
            <p className={styles.subtitle}>{t('admin.project_form_subtitle') || 'Configura la información del proyecto.'}</p>
          </div>
        </div>

        <button type="submit" form="dev-form" className={styles.primaryButton} disabled={saving}>
          <FiSave /> {saving ? (t('admin.saving') || 'Guardando...') : (t('admin.save') || 'Guardar')}
        </button>
      </header>

      {error ? (
        <div className={styles.errorBanner} role="alert">
          {error}
        </div>
      ) : null}

      {isDeveloper && mode === 'create' ? (
        <div className={styles.infoBanner}>
          <strong>Después de guardar, sube los documentos del proyecto.</strong>
          <span>
            El desarrollo quedará en borrador hasta que un administrador revise la documentación y lo apruebe para publicarlo.
          </span>
        </div>
      ) : null}

      <form id="dev-form" onSubmit={onSubmit} className={styles.formCard}>
        <div className={styles.grid}>
          <div className={styles.field}>
            <label className={styles.label}>
              Nombre <span className={styles.req}>*</span>
            </label>
            <input
              className={styles.input}
              value={form.name}
              onChange={e => update('name', e.target.value)}
              placeholder="Nombre del proyecto"
            />
          </div>

          <div className={styles.field}>
            <label className={styles.label}>Slug</label>
            <input
              className={styles.input}
              value={form.slug || ''}
              placeholder="slug-del-proyecto"
              disabled
              readOnly
            />
          </div>

          <div className={styles.field}>
            <label className={styles.label}>
              País <span className={styles.req}>*</span>
            </label>
            <input
              className={styles.input}
              value={form.country}
              onChange={e => update('country', e.target.value)}
              placeholder="País"
            />
          </div>

          <div className={styles.field}>
            <label className={styles.label}>
              Estado <span className={styles.req}>*</span>
            </label>
            <input
              className={styles.input}
              value={form.state}
              onChange={e => update('state', e.target.value)}
              placeholder="Estado"
            />
          </div>

          <div className={styles.field}>
            <label className={styles.label}>
              Ciudad <span className={styles.req}>*</span>
            </label>
            <input
              className={styles.input}
              value={form.city}
              onChange={e => update('city', e.target.value)}
              placeholder="Ciudad"
            />
          </div>

          <div className={styles.field}>
            <label className={styles.label}>Dirección</label>
            <input
              className={styles.input}
              value={form.address || ''}
              onChange={e => update('address', e.target.value)}
              placeholder="Dirección"
            />
          </div>

          <div className={styles.field}>
            <label className={styles.label}>Estatus</label>
            <select className={styles.select} value={form.status} onChange={e => update('status', e.target.value as any)}>
              {statusOptions.map(o => (
                <option key={o.value} value={o.value}>
                  {o.label}
                </option>
              ))}
            </select>
          </div>

          <div className={styles.field}>
            <label className={styles.label}>Progreso (%)</label>
            <input
              className={styles.input}
              type="number"
              value={Number(form.progress_pct ?? 0)}
              onChange={e => update('progress_pct', e.target.value === '' ? null : Number(e.target.value))}
              min={0}
              max={100}
            />
          </div>

          <div className={styles.fieldWide}>
            <label className={styles.label}>Resumen</label>
            <textarea
              className={styles.textarea}
              value={form.summary || ''}
              onChange={e => update('summary', e.target.value)}
              rows={2}
            />
          </div>

          <div className={styles.fieldWide}>
            <label className={styles.label}>Descripción</label>
            <textarea
              className={styles.textarea}
              value={form.description || ''}
              onChange={e => update('description', e.target.value)}
              rows={5}
            />
          </div>

          <div className={styles.fieldWide}>
            <label className={styles.label}>OG Image URL</label>
            <input
              className={styles.input}
              value={form.og_image_url || ''}
              onChange={e => update('og_image_url', e.target.value)}
              placeholder="https://..."
            />
          </div>

          <div className={styles.fieldWide}>
            <label className={styles.label}>Video YouTube (URL)</label>
            <input
              className={styles.input}
              value={form.video_url || ''}
              onChange={e => update('video_url', e.target.value)}
              placeholder="https://www.youtube.com/watch?v=..."
            />
          </div>

          <div className={styles.field}>
            <label className={styles.label}>Estado del proyecto</label>
            <select
              className={styles.select}
              value={isDeveloper && mode === 'create' ? 'DRAFT' : (form.project_state || 'DRAFT')}
              onChange={e => update('project_state', e.target.value as ProjectState)}
              disabled={isDeveloper}
            >
              {projectStateOptions.map(o => (
                <option key={o.value} value={o.value}>
                  {o.label}
                </option>
              ))}
            </select>
          </div>

          <div className={styles.field}>
            <label className={styles.label}>Inicio de inversión</label>
            <input
              className={styles.input}
              type="datetime-local"
              value={toDateTimeLocal(form.investment_start_at)}
              onChange={e => update('investment_start_at', e.target.value || null)}
            />
          </div>

          <div className={styles.field}>
            <label className={styles.label}>Cierre de inversión</label>
            <input
              className={styles.input}
              type="datetime-local"
              value={toDateTimeLocal(form.investment_end_at)}
              onChange={e => update('investment_end_at', e.target.value || null)}
            />
          </div>

          <div className={styles.field}>
            <label className={styles.label}>Mín. bloques por compra</label>
            <input
              className={styles.input}
              type="number"
              value={Number(form.min_blocks_per_order ?? 0)}
              onChange={e => update('min_blocks_per_order', e.target.value === '' ? null : Number(e.target.value))}
              min={0}
            />
          </div>

          <div className={styles.field}>
            <label className={styles.label}>Máx. bloques por usuario</label>
            <input
              className={styles.input}
              type="number"
              value={Number(form.max_blocks_per_user ?? 0)}
              onChange={e => update('max_blocks_per_user', e.target.value === '' ? null : Number(e.target.value))}
              min={0}
            />
          </div>

          <div className={styles.field}>
            <label className={styles.label}>Objetivo de fondeo ($ MXN)</label>
            <input
              className={styles.input}
              type="number"
              inputMode="decimal"
              step="0.01"
              value={Number(form.funding_goal ?? 0)}
              onChange={e => update('funding_goal', e.target.value === '' ? null : Number(e.target.value))}
              min={0}
            />
          </div>

          <div className={styles.field}>
            <label className={styles.label}>Plazo (meses)</label>
            <input
              className={styles.input}
              type="number"
              value={Number(form.term_months ?? 0)}
              onChange={e => update('term_months', e.target.value === '' ? null : Number(e.target.value))}
              min={0}
            />
          </div>

          <div className={styles.fieldWide}>
            <label className={styles.label}>{t('admin.blocks_section_title') || 'Configuración de bloques'}</label>
            <div className={styles.grid}>
              <div className={styles.field}>
                <label className={styles.label}>Precio por bloque ($ MXN)</label>
                <input
                  className={styles.input}
                  type="number"
                  inputMode="decimal"
                  step="0.01"
                  value={Number(block.unit_price || 0)}
                  onChange={e => setBlock(prev => ({ ...prev, unit_price: Number(e.target.value || 0) }))}
                  min={0}
                />
              </div>
              <div className={styles.field}>
                <label className={styles.label}>Total de bloques</label>
                <input
                  className={styles.input}
                  type="number"
                  value={Number(block.available_qty || 0)}
                  onChange={e => setBlock(prev => ({ ...prev, available_qty: Number(e.target.value || 0) }))}
                  min={0}
                />
              </div>
              <div className={styles.field}>
                <label className={styles.label}>Descripción</label>
                <input
                  className={styles.input}
                  value={block.description}
                  onChange={e => setBlock(prev => ({ ...prev, description: e.target.value }))}
                />
              </div>
            </div>
          </div>

          <div className={styles.fieldWide}>
            <label className={styles.label}>PDFs</label>
            <div className={styles.grid}>
              <div className={styles.field}>
                <label className={styles.label}>Título</label>
                <input
                  className={styles.input}
                  value={docTitle}
                  onChange={e => setDocTitle(e.target.value)}
                />
              </div>
              <div className={styles.field}>
                <label className={styles.label}>URL</label>
                <input
                  className={styles.input}
                  value={docUrl}
                  onChange={e => setDocUrl(e.target.value)}
                  placeholder="https://..."
                />
              </div>
            </div>
            <button type="button" className={styles.ghostButton} onClick={addDocument}>
              Agregar PDF
            </button>

            <div style={{ marginTop: 8 }}>
              <label className={styles.label}>Subir PDF(s)</label>
              <input
                className={styles.input}
                type="file"
                accept="application/pdf"
                multiple
                onChange={e => onSelectDocs(e.target.files)}
              />
            </div>

            {docFiles.length > 0 ? (
              <div style={{ marginTop: 8, fontSize: 12, opacity: 0.7 }}>
                {docFiles.map(f => f.name).join(', ')}
              </div>
            ) : null}

            {documents.length > 0 ? (
              <div style={{ marginTop: 12, display: 'grid', gap: 8 }}>
                {documents.map(doc => (
                  <div key={doc.id} className={styles.rowCard}>
                    <div>
                      <strong>{doc.title}</strong>
                      <div style={{ fontSize: 12, opacity: 0.7 }}>{doc.file_url}</div>
                    </div>
                    <button type="button" className={styles.ghostButton} onClick={() => removeDocument(doc)}>
                      Quitar
                    </button>
                  </div>
                ))}
              </div>
            ) : null}
          </div>

          <div className={styles.fieldWide}>
            <label className={styles.label}>Galería de imágenes (máx 3)</label>
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
              <button
                type="button"
                className={styles.ghostButton}
                onClick={() => setImageMode('upload')}
              >
                Subir imágenes
              </button>
              <button
                type="button"
                className={styles.ghostButton}
                onClick={() => setImageMode('links')}
              >
                Pegar links
              </button>
            </div>

            {imageMode === 'upload' ? (
              <>
                <input
                  className={styles.input}
                  type="file"
                  accept="image/png,image/jpeg,image/jpg,image/webp"
                  multiple
                  onChange={e => onSelectImages(e.target.files)}
                />
                {imagePreviews.length > 0 ? (
                  <div style={{ marginTop: 10, display: 'grid', gap: 8 }}>
                    {imagePreviews.map((p, idx) => (
                      <div key={p.url} className={styles.rowCard}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                          <img src={p.url} alt={p.file.name} style={{ width: 64, height: 48, objectFit: 'cover', borderRadius: 8 }} />
                          <div style={{ fontSize: 12 }}>{p.file.name}</div>
                        </div>
                        <div style={{ display: 'flex', gap: 6 }}>
                          <button
                            type="button"
                            className={styles.ghostButton}
                            onClick={() => moveNewImage(idx, -1)}
                          >
                            ↑
                          </button>
                          <button
                            type="button"
                            className={styles.ghostButton}
                            onClick={() => moveNewImage(idx, 1)}
                          >
                            ↓
                          </button>
                          <button
                            type="button"
                            className={styles.ghostButton}
                            onClick={() => setImageCoverIndex(idx)}
                          >
                            {imageCoverIndex === idx ? 'Portada' : 'Marcar portada'}
                          </button>
                          <button type="button" className={styles.ghostButton} onClick={() => {
                            const next = [...imageFiles];
                            next.splice(idx, 1);
                            setImageFiles(next);
                          }}>
                            Quitar
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : null}
              </>
            ) : (
              <div style={{ marginTop: 8, display: 'grid', gap: 8 }}>
                {imageLinks.map((link, idx) => (
                  <input
                    key={`link-${idx}`}
                    className={styles.input}
                    value={link}
                    placeholder={`Link imagen ${idx + 1}`}
                    onChange={e => updateImageLink(idx, e.target.value)}
                  />
                ))}
              </div>
            )}

            {existingImages.length > 0 ? (
              <div style={{ marginTop: 12, display: 'grid', gap: 8 }}>
                {existingImages.map((img, idx) => (
                  <div key={img.id} className={styles.rowCard}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                      <img src={img.image_url} alt={img.alt || `Imagen ${idx + 1}`} style={{ width: 64, height: 48, objectFit: 'cover', borderRadius: 8 }} />
                      <div style={{ fontSize: 12 }}>{img.image_url}</div>
                    </div>
                    <div style={{ display: 'flex', gap: 6 }}>
                      <button type="button" className={styles.ghostButton} onClick={() => moveExisting(idx, -1)}>
                        ↑
                      </button>
                      <button type="button" className={styles.ghostButton} onClick={() => moveExisting(idx, 1)}>
                        ↓
                      </button>
                      <button type="button" className={styles.ghostButton} onClick={() => setExistingCover(img.id)}>
                        {img.is_cover ? 'Portada' : 'Marcar portada'}
                      </button>
                      <button type="button" className={styles.ghostButton} onClick={() => removeExistingImage(img.id)}>
                        Quitar
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            ) : null}
          </div>

          <div className={styles.switchRow}>
            <label className={styles.switch}>
              <input
                type="checkbox"
                checked={Boolean(form.is_active)}
                onChange={e => update('is_active', e.target.checked)}
              />
              <span className={styles.switchTrack} />
              <span className={styles.switchLabel}>Activo</span>
            </label>
          </div>
        </div>

        <div className={styles.formActions}>
          <button type="submit" className={styles.primaryButton} disabled={saving}>
            <FiSave /> {saving ? (t('admin.saving') || 'Guardando...') : (t('admin.save') || 'Guardar')}
          </button>
        </div>
      </form>
    </div>
  );
}
